import { useCallback, useEffect, useRef, useState } from "react";
import { Amplify } from "aws-amplify";
import { events } from "aws-amplify/data";
import { ChatRoom, ChatEvent } from "amazon-ivs-chat-messaging";
import { useQueryClient } from "@tanstack/react-query";
import { useAppSelector } from "@src/redux/hooks";
import { getEventsToken } from "@src/apis/chatApis";
import { getCuratorConversationRealtimeConfig } from "@src/apis/curatorConversationRealtimeApis";
import { IVS_CHAT_REGION } from "@src/constants/ivsChat";
import { ICuratorConversation } from "@src/types/curator";
import {
  CuratorConversationRealtimeConfig,
  CuratorConversationRealtimeEvent,
} from "@src/types/curatorConversationRealtime";
import { patchConversationListFromRealtimeEvent } from "@src/pages/Messages/conversationUtils";

const REFETCH_DEBOUNCE_MS = 400;

type EventsChannel = {
  close: () => void;
};

function parseRealtimeEvent(data: unknown): CuratorConversationRealtimeEvent {
  const payload = data as { event?: CuratorConversationRealtimeEvent };
  if (payload?.event && typeof payload.event === "object") {
    return payload.event;
  }

  return (data as CuratorConversationRealtimeEvent) ?? {};
}

function shouldUseAppSyncConfig(
  config: CuratorConversationRealtimeConfig | null
): config is CuratorConversationRealtimeConfig {
  return config?.provider === "appsync" && config.enabled === true;
}

/**
 * Subscribes to curator conversation application events.
 * Uses AWS AppSync Events when enabled by the backend, otherwise falls back
 * to the legacy IVS events room during dual-run migration.
 */
export function useCuratorConversationEvents(
  curatorDocumentId: string | undefined,
  enabled = true
) {
  const queryClient = useQueryClient();
  const authToken = useAppSelector((state) => state.auth.token);
  const [isEventsConnected, setIsEventsConnected] = useState(false);

  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isMountedRef = useRef(true);
  const roomRef = useRef<ChatRoom | null>(null);
  const appSyncChannelsRef = useRef<EventsChannel[]>([]);
  const amplifyConfiguredRef = useRef(false);

  const scheduleListRefetch = useCallback(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = setTimeout(() => {
      if (!isMountedRef.current) return;
      queryClient.invalidateQueries({ queryKey: ["conversations"] });
    }, REFETCH_DEBOUNCE_MS);
  }, [queryClient]);

  const updateConversationRow = useCallback(
    (event: CuratorConversationRealtimeEvent) => {
      if (!event.conversationId) {
        scheduleListRefetch();
        return;
      }

      queryClient.setQueryData<ICuratorConversation[]>(
        ["conversations"],
        (current: ICuratorConversation[] | undefined) => {
          if (!current?.length) {
            scheduleListRefetch();
            return current;
          }

          return patchConversationListFromRealtimeEvent(current, event);
        }
      );

      scheduleListRefetch();
    },
    [queryClient, scheduleListRefetch]
  );

  const handleRealtimeEvent = useCallback(
    (event: CuratorConversationRealtimeEvent) => {
      switch (event.eventName) {
        case "newRequest":
          scheduleListRefetch();
          break;
        case "statusChanged":
          scheduleListRefetch();
          break;
        case "newMessage":
          updateConversationRow(event);
          break;
        default:
          break;
      }
    },
    [scheduleListRefetch, updateConversationRow]
  );

  const handleRealtimeEventRef = useRef(handleRealtimeEvent);

  useEffect(() => {
    handleRealtimeEventRef.current = handleRealtimeEvent;
  }, [handleRealtimeEvent]);

  useEffect(() => {
    isMountedRef.current = true;

    if (!enabled || !curatorDocumentId || !authToken) {
      setIsEventsConnected(false);
      return () => {
        isMountedRef.current = false;
      };
    }

    let cancelled = false;
    const unsubscribeFns: Array<() => void> = [];

    const connectLegacyIvs = async () => {
      const room = new ChatRoom({
        regionOrUrl: IVS_CHAT_REGION,
        tokenProvider: async () => {
          const data = await getEventsToken();
          return {
            token: data.token,
            sessionExpirationTime: new Date(data.sessionExpirationTime),
            tokenExpirationTime: new Date(data.tokenExpirationTime),
          };
        },
      });

      if (cancelled) return;

      roomRef.current = room;

      unsubscribeFns.push(
        room.addListener("connect", () => {
          if (!cancelled && isMountedRef.current) {
            setIsEventsConnected(true);
          }
        })
      );

      unsubscribeFns.push(
        room.addListener("disconnect", () => {
          if (!cancelled && isMountedRef.current) {
            setIsEventsConnected(false);
          }
        })
      );

      unsubscribeFns.push(
        room.addListener("event", (event: ChatEvent) => {
          if (cancelled || !isMountedRef.current) return;

          const roomId = event.attributes?.roomId;
          if (roomId !== curatorDocumentId) return;

          handleRealtimeEventRef.current({
            eventName: event.eventName,
            conversationId: event.attributes?.conversationId,
          });
        })
      );

      try {
        await room.connect();
      } catch (error) {
        if (!cancelled && isMountedRef.current) {
          setIsEventsConnected(false);
          // eslint-disable-next-line no-console
          console.error("Legacy IVS events socket error:", error);
        }
      }
    };

    const connectAppSync = async (
      config: CuratorConversationRealtimeConfig
    ) => {
      if (!amplifyConfiguredRef.current) {
        Amplify.configure({
          API: {
            Events: {
              endpoint: config.endpoint,
              region: config.region,
              defaultAuthMode: config.authMode,
            },
          },
        });
        amplifyConfiguredRef.current = true;
      }

      const channelPath = `${config.namespace}/user/${curatorDocumentId}`;
      const channel = await events.connect(channelPath, {
        authMode: "lambda",
        authToken,
      });

      if (cancelled) {
        channel.close();
        return;
      }

      appSyncChannelsRef.current.push(channel);
      setIsEventsConnected(true);

      channel.subscribe({
        next: (data) => {
          if (cancelled || !isMountedRef.current) return;
          handleRealtimeEventRef.current(parseRealtimeEvent(data));
        },
        error: (error) => {
          if (!cancelled && isMountedRef.current) {
            setIsEventsConnected(false);
            // eslint-disable-next-line no-console
            console.error("AppSync subscription error:", error);
          }
        },
      });
    };

    const connect = async () => {
      try {
        const config = await getCuratorConversationRealtimeConfig();
        if (cancelled) return;

        if (shouldUseAppSyncConfig(config)) {
          await connectAppSync(config);
          return;
        }

        await connectLegacyIvs();
      } catch (error) {
        if (!cancelled && isMountedRef.current) {
          setIsEventsConnected(false);
          // eslint-disable-next-line no-console
          console.error("Curator conversation realtime connect error:", error);
        }
      }
    };

    connect();

    return () => {
      cancelled = true;
      isMountedRef.current = false;

      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = null;
      }

      unsubscribeFns.forEach((unsubscribe) => unsubscribe());

      if (roomRef.current) {
        roomRef.current.disconnect();
        roomRef.current = null;
      }

      appSyncChannelsRef.current.forEach((channel) => channel.close());
      appSyncChannelsRef.current = [];

      setIsEventsConnected(false);
    };
  }, [authToken, curatorDocumentId, enabled]);

  return { isEventsConnected };
}
